import { ConfirmationService } from 'primeng/api';
import { Component, Input, OnChanges, ViewChild, ElementRef, OnInit, HostListener } from '@angular/core';
import { ElementModel } from '../../../models/element.model';
import { MARKER_COLORS } from '../../../app.constants';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { AuthStateService } from '../../../services/auth-state.service';
import { v4 as uuidv4 } from 'uuid';
import { ApiService } from '../../../services/api.service';
import { UiService } from '../../../services/ui.service';
import { CandidateModel } from '../../../models/candidate.model';
import { DataService } from '../../../services/data.service';
import { MarkModel, MarkLayerModel } from '../../../models/mark.model';
import { TranslocoService } from '@ngneat/transloco';
import { decode } from 'html-entities';
import { AuthUserModel } from 'projects/core/src/lib/models/authuser.model';

const SEL_START = 'sel_start';
const SEL_END = 'sel_end';

@Component({
  selector: 'app-content-container',
  templateUrl: './content-container.component.html',
  styleUrls: ['./content-container.component.scss']
})
export class ContentContainerComponent implements OnInit, OnChanges {

  @Input() element: ElementModel = null;
  @ViewChild('comment') private comment: ElementRef;

  public candidate: CandidateModel = null;

  public headline: string = null;
  public content: any = null;
  public marks: Array<MarkModel> = [];
  public showMarks = true;
  public markerPos: any = {
    top: '-10000px',
    left: '-10000px'
  };
  public markerColors = MARKER_COLORS;
  public dialogVisible = false;
  public dialogMode: string = null;
  public activeMark: MarkModel = null;
  public activeChapter: any = null;
  public resizeTimer: any;
  public flatChapters: Array<any> = [];

  faCheck = faCheck;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.showMarks = false;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.renderMarks();
    }, 300);
  }

  constructor(
    private api: ApiService,
    private data: DataService,
    private auth: AuthStateService,
    private ui: UiService,
    private confirm: ConfirmationService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.auth.getAuthUser().subscribe((user: AuthUserModel) => {
      this.candidate = this.data.ac.getCandidate(user?.hashed_id);
      this.renderMarks();
    });
  }

  ngOnChanges(): void {
    if (this.element !== null) {
      this.flatChapters = [];
      this.makeFlatChapters(this.element.content);
      this.showChapter(this.element.activeChapter?.id || null);
    }
  }

  public showChapter(id: string = null): void {
    this.headline = '';
    this.content = '';
    if (this.element?.content?.length > 0) {
      let chapter = this.flatChapters.find((actual: any) => actual.id === id);
      if (chapter === undefined) {
        chapter = this.flatChapters[0];
      }
      if (chapter) {
        this.activeChapter = chapter;
        this.element.activeChapter = chapter;
        this.headline = chapter.name;
        this.content = this.cleanContent(chapter.content);
        this.renderMarks();
      }
    }
  }

  public onRightClick(event: any): void {
    if (!this.auth.isModerator()) {
      event.preventDefault();
    }
  }

  public onMouseUp(event: MouseEvent): void {
    if (this.auth.isCandidate() && this.dialogVisible !== true && window.getSelection) {
      const selection = window.getSelection();
      if (selection.type === 'Range') {
        this.setSelection(selection.getRangeAt(0).cloneRange(), selection.getRangeAt(0).cloneRange());
      } else {
        this.removeSelection();
      }

    }
  }

  public showCommentDialog(mark: MarkModel = null): void {
    if (mark === null) {
      this.dialogMode = 'new';
      const container = document.getElementById('content');
      const startElement = document.getElementById(SEL_START);
      const endElement = document.getElementById(SEL_END);
      if (container && startElement && endElement) {
        const content = this.cleanContent(container.innerHTML);
        const start = content.indexOf(startElement.outerHTML);
        const end = content.indexOf(endElement.outerHTML) - startElement.outerHTML.length;
        const chapter = this.flatChapters.find((actual: any) => actual.id === this.activeChapter.id);
        this.activeMark = new MarkModel().deserialize({
          element_id: this.element.id,
          chapter: chapter.id,
          uuid: uuidv4(),
          start,
          end,
          color: MARKER_COLORS[0],
          notices: ''
        });
        this.dialogVisible = true;
      }
    } else {
      this.dialogMode = 'edit';
      this.activeMark = new MarkModel().deserialize({...mark});
      this.dialogVisible = true;
    }
  }

  public hideCommentDialog(): void {
    this.dialogMode = null;
    this.activeMark = null;
    this.dialogVisible = false;
  }

  public deleteMark(): void {
    if (this.activeMark !== null) {
      this.confirm.confirm({
        message: this.transloco.translate('partials.content_container.messages.really_delete'),
        header: this.transloco.translate('partials.content_container.messages.delete'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.transloco.translate('common.labels.yes'),
        rejectLabel: this.transloco.translate('common.labels.no'),
        accept: () => {
          this.api.deleteMark(this.activeMark.uuid).subscribe({
            next: () => {
              // this.ui.toastSuccess('Der Kommentar wurde gelöscht.', this.transloco.translate('common.deleted'));
            },
            error: (error: any) => {
              this.ui.toastError(error.message);
            }
          }).add(() => {
            const mark = this.candidate.marks.find((actual: MarkModel) => {
              return actual.uuid === this.activeMark.uuid;
            });
            this.candidate.marks.splice(this.candidate.marks.indexOf(mark), 1);
            this.marks = this.candidate.marks;
            this.renderMarks();
            this.removeSelection();
            this.hideCommentDialog();
          });
        }
      });
    }
  }

  public setActiveColor(color: string): void {
    if (this.activeMark !== null) {
      this.activeMark.color = color;
    }
  }

  public isValid(): boolean {
    return true; // this.activeMark?.notices?.length > 0;
  }

  public saveComment(): void {
    let mark: MarkModel = null;
    if (this.dialogMode === 'edit') {
      mark = this.marks.find((actual: MarkModel) => {
        return actual.uuid === this.activeMark.uuid;
      });
      mark.deserialize({...this.activeMark});
    }
    if (mark === null) {
      mark = new MarkModel().deserialize({...this.activeMark});
      this.candidate.marks.push(mark);
    }
    this.api.saveMark(mark).subscribe({
      next: () => {
      },
      error: () => {
        this.ui.toastError(this.transloco.translate('partials.content_container.messages.not_saved'));
      }
    }).add(() => {
      this.marks = this.candidate.marks;
      this.renderMarks();
      this.removeSelection();
      this.hideCommentDialog();
    });
  }

  private setSelection(startRange: any, endRange: any): void {
    if (startRange && endRange) {
      this.removeSelection();
      // start
      startRange.collapse(true);
      const startElement = document.createElement('span');
      startElement.id = SEL_START;
      startElement.appendChild(document.createTextNode('\ufeff'));
      startRange.insertNode(startElement);
      // end
      endRange.collapse(false);
      const endElement = document.createElement('span');
      endElement.id = SEL_END;
      endElement.appendChild(document.createTextNode('\ufeff'));
      endRange.insertNode(endElement);
      this.markerPos.top = '' + endElement.offsetTop + 'px';
      this.markerPos.left = '' + endElement.offsetLeft + 'px';
    }
  }

  private removeSelection(): void {
    if (document.getElementById(SEL_START)) {
      document.getElementById(SEL_START).remove();
    }
    if (document.getElementById(SEL_END)) {
      document.getElementById(SEL_END).remove();
    }
    this.markerPos.top = '-10000px';
    this.markerPos.left = '-10000px';
  }

  private renderMarks(): void {
    this.marks = [];
    if (this.content && this.candidate) {
      this.marks = this.candidate.marks.filter(mark => mark.chapter === this.activeChapter.id);
      // remap marks
      const markList = [];
      this.marks.forEach((mark: any, index: number) => {
        if (mark.chapter === this.activeChapter.id) {
          markList.push({
            id: 'start_' + index,
            position: mark.start,
            type: 'start',
            notices: mark.notices
          });
          markList.push({
            id: 'end_' + index,
            position: mark.end,
            type: 'end'
          });
        }
      });

      const backupContent = this.content;
      let tempContent = this.cleanContent(this.content);
      const tagList = this.getTaglist(tempContent);
      markList.sort((a: any, b: any) => (a.position > b.position) ? -1 : ((b.position > a.position) ? 1 : 0));
      markList.forEach((entry: any) => {
        const position = this.adjustPosition(tagList, entry.position, entry.type);
        tempContent = tempContent.substring(0, position)
          + '<span id="' + entry.id + '">\ufeff</span>'
          + tempContent.substring(position);
      });
      this.content = tempContent;
      setTimeout(() => {
        this.marks.forEach((mark: MarkModel, index: number) => {
          const startMarker = document.getElementById('start_' + index);
          const endMarker = document.getElementById('end_' + index);
          if (startMarker && endMarker) {
            mark.layers = this.getMarkLayers(startMarker, endMarker, mark.color, mark.notices);
          }
        });
        this.content = backupContent;
        this.showMarks = true;
      }, 100);
    }
  }

  private getTaglist(content: string): Array<{start: number, end: number}> {
    const tagList = [];
    let start: number;
    for (let i = 0; i < content.length; i++) {
      if (content.charAt(i) === '<') {
        start = i;
      } else if (content.charAt(i) === '>') {
        tagList.push({
          start,
          end: i
        });
      }
    }
    return tagList;
  }

  private adjustPosition(tagList: Array<{start: number, end: number}>, position: number, type: string): number {
    const temp = 0 + position;
    const foundTag = tagList.find((tag: {start: number, end: number}) => {
      return position > tag.start && position <= tag.end;
    });
    if (foundTag !== undefined) {
      if (type === 'start') {
        position = foundTag.end + 1;
      } else if (type === 'end') {
        position = foundTag.start;
      }
    }
    return position;
  }

  private getMarkLayers(startMarker: any, endMarker: any, color: any, notices: string): Array<MarkLayerModel> {
    const layers = [];
    const markersContainer = document.getElementById('markers');
    const containerWidth = markersContainer.offsetWidth;
    if (startMarker.offsetTop === endMarker.offsetTop) {
      // SINGLE
      layers.push({
        visible: false,
        top: startMarker.offsetTop + 'px',
        left: startMarker.offsetLeft + 'px',
        width: Math.abs(endMarker.offsetLeft - startMarker.offsetLeft) + 'px',
        height: endMarker.offsetHeight + 'px',
        color,
        notices
      });
    } else {
      if ((startMarker.offsetTop + startMarker.offsetHeight) < endMarker.offsetTop) {
        // TRIPLE
        // top
        layers.push({
          visible: false,
          top: startMarker.offsetTop + 'px',
          left: startMarker.offsetLeft + 'px',
          width: (containerWidth - startMarker.offsetLeft) + 'px',
          height: startMarker.offsetHeight + 'px',
          color
        });
        // middle
        layers.push({
          visible: false,
          top: (startMarker.offsetTop + startMarker.offsetHeight) + 'px',
          left: '0px',
          width: containerWidth + 'px',
          height: Math.abs(endMarker.offsetTop - (startMarker.offsetTop + startMarker.offsetHeight)) + 'px',
          color,
          notices
        });
        // botton
        layers.push({
          visible: false,
          top: endMarker.offsetTop + 'px',
          left: '0px',
          width: endMarker.offsetLeft + 'px',
          height: endMarker.offsetHeight + 'px',
          color
        });
      } else {
        // DOUBLE
        // top
        layers.push({
          visible: false,
          top: startMarker.offsetTop + 'px',
          left: startMarker.offsetLeft + 'px',
          width: (containerWidth - startMarker.offsetLeft) + 'px',
          height: startMarker.offsetHeight + 'px',
          color,
          notices
        });
        // botton
        layers.push({
          visible: false,
          top: endMarker.offsetTop + 'px',
          left: '0px',
          width: endMarker.offsetLeft + 'px',
          height: endMarker.offsetHeight + 'px',
          color,
          notices
        });
      }

    }
    return layers;
  }

  private cleanContent(content: string): string {
    content = content.replace(/[\n\r\t]/gm, '');
    content = decode(content, { level: 'html5' });
    content = content.replace(/[\s]*\/>/, '>');
    // set video autoplay
    if (this.element.misc?.['autoplay'] !== 1) {
      content = content.replace(/autoplay(="[^"]*")*/gm, '');
    }
    return content;
  }

  private makeFlatChapters(chapters: Array<any>): void {
    if (chapters?.length > 0) {
      chapters.forEach((chapter: any) => {
        this.flatChapters.push({
          id: chapter.id,
          name: chapter.name,
          content: chapter.content
        });
        this.makeFlatChapters(chapter.children);
      });
    }
  }

}
